import { GridColDef } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { atom, useSetAtom } from 'jotai';
import {
  useAllChoiceGroup,
  useDeleteChoiceGroup,
  usePatchChoiceGroup,
} from '../../Query/ChoiceGroupQuery';
import AddIcon from '@mui/icons-material/Add';

import React, { useState, useCallback, useMemo,useEffect } from 'react';
import { COLORS } from '../../../theme/themeConstants';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
import AddChoiceGroup from './AddChoiceGroup';
import { AddOrUpdateChoiceGroupRequestSchema } from '../../../client-sdk/api';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import { useManageCmsParams } from '../ManageCMS/useManageCmsParams';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import ErrorHandler from '../../Stateless/Common/ErrorHandler';
import { useDebounce } from '../Utils/useDounce';
import PermissionActionButtons from '../../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../../context/PermissionContext';

type StoreWithSno = Partial<AddOrUpdateChoiceGroupRequestSchema> & {
  id: string;
  sno: number;
  name?: string;
  merchant?: string;
  storeType?: string;
  category?: string;
  addedBy?: string;
  status?: string;
  // choiceGroupOption? : string | undefined;
};
export interface ChoiceGroupDataType {
  storeType: any;
  choiceGroupNameEnglish: string;
  choiceGroupNameArabic: string;
  minimumLimit: number | null;
  maximumLimit: number | null;
  choiceDetails: any;
  choiceGroupOption: any;
  merchantDetails: any;
  id: any;
  storeId?: string;
}

export const choiceGroupAtom = atom<ChoiceGroupDataType | null>(null);

// type StoreWithSno = Partial<WritableRestaurantDataSchema> & {
//   id: string;
//   sno: number;
// };

const ManageChoiceGroupTable = () => {
  
   const setChoiceGroupAtom = useSetAtom(choiceGroupAtom);
  const { showMessage } = useSnackbar();
  const { hasPermission } = usePermissions();
  // const { getActionColumns } = usePermissionBasedColumns();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const snackbarMessage = ''; // Replace with actual message
  const snackbarSeverity: 'success' | 'error' | 'info' | 'warning' = 'info';
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [modalContent] = useState<'add' | 'edit'>('edit');


    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 400);

  const { params, dispatch } = useManageCmsParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  const { data: ChoiceGroupData, isLoading, refetch } = useAllChoiceGroup(memoizedParams);


  const { mutate: patchChoiceGroup } = usePatchChoiceGroup();

  const { mutate: deleteChoiceGroup } = useDeleteChoiceGroup();
  const filteredRows = React.useMemo(() => {
    const storeOwners = ChoiceGroupData?.data || [];

    // Add sno field to each row and ensure all required fields are present
    return storeOwners.map((row, index) => {
      // Ensure we have a valid row object
      if (!row) return { id: `empty-${index}`, sno: index + 1 } as StoreWithSno;

      // Create a new object with all the row properties plus sno
      const rowWithSno = {
        ...row,
        id: row.choiceGroupId,
        sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        name: row.storeName?? 'N/A',
        merchantType: row.storeType ?? 'N/A',
        ChoiceGroup: row.choiceGroupNameEnglish,
        minimumLimit: row.minimumLimit,
        maximumLimit: row.maximumLimit,
        choiceDetails: row.choiceDetails.map((detail) => detail.choiceId),
        // choiceGroupOption: row.choiceGroupOption === "Optional" ? true : row.choiceGroupOption === "Required" ? false : undefined,
      };

      return rowWithSno as StoreWithSno;
    });
  }, [params.search, ChoiceGroupData, params.page, params.limit]);


 
   const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
     setSearchTerm(e.target.value)
   };
 
   useEffect(() => {
     dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
   }, [debouncedSearch]);


  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  const handleBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one choice group to block', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to block?');
      if (!confirmed) return;

      patchChoiceGroup({
        id: selectedRows,
        data: { status: 'blocked' },
      });
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage(
        'Please select at least one choice group to unblock',
        'error'
      );
    } else {
      const confirmed = window.confirm('Are you sure you want to unblock?');
      if (!confirmed) return;

      patchChoiceGroup({
        id: selectedRows,
        data: { status: 'unblocked' },
      });
    }
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one choice group to delete', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteChoiceGroup(selectedRows, {
        onSuccess: () => {
          showMessage('choice group deleted successfully', 'success');
          setSelectedRows([]);
        },
        onError: () => showMessage('Error deleting categories', 'error'),
      });
    }
  }, [selectedRows,showMessage,deleteChoiceGroup]);

  const handleDeleteButton = useCallback(
    (id: string) => {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteChoiceGroup([id]);
    },
    [selectedRows, deleteChoiceGroup]
  );

  const handleAddSubAdmin = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleEditClick = (row: any) => {
    setChoiceGroupAtom(row);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setChoiceGroupAtom(null);
  };

  const handleBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to block this Choice group?'
    );
    if (!confirmed) return;

    await patchChoiceGroup({
      id: [id],
      data: { status: 'blocked' },
    });
  };
  const handleUnBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to unblock this Choice group?'
    );
    if (!confirmed) return;

    await patchChoiceGroup({
      id: [id],
      data: { status: 'unblocked' },
    });
  };

  // Determine which bulk buttons to show based on selected rows' statuses
  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = ChoiceGroupData?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.choiceGroupId), String(item.status || '')])
    );
    const statuses = selectedRows.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return {
      showBlock: hasUnblocked,
      showUnblock: hasBlocked,
    };
  }, [selectedRows, ChoiceGroupData]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    const id = typeof row.id === 'string' ? row.id : '';
    const canEdit = hasPermission('choiceGroup', 'Edit');
    const canDelete = hasPermission('choiceGroup', 'Delete');

    // Don't render the actions menu if user has no permissions
    if (!canEdit && !canDelete) return null;

    return (
      <>
        <IconButton size="small" onClick={handleOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {canEdit && (
            <MenuItem
              onClick={() => {
                handleClose();
                handleEditClick(row);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem
              onClick={() => {
                handleClose();
                if (id) {
                  handleDeleteButton(id);
                }
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'sno',
      headerName: 'S.No',
      flex: 0.3,
    },
    {
      field: 'name',
      headerName: 'Store Name',
      flex: 2,
    },
    {
      field: 'storeType',
      headerName: 'Store Type',
      flex: 2,
    },
    {
      field: 'ChoiceGroup',
      headerName: 'Choice Group',
      flex: 1,
    },
    {
      field: 'minimumLimit',
      headerName: 'Minimum Limit',
      flex: 1,
    },
    {
      field: 'maximumLimit',
      headerName: 'Maximum Limit',
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const isBlocked = params.value === 'blocked';
        const id = params.row.id;
        const validId = typeof id === 'string' ? id : '';
        const action = isBlocked ? 'Unblock' : 'Block';
        
        // Check if user has permission for the specific action
        if (!hasPermission('choiceGroup', action)) {
          return null;
        }

        return (
          <IconButton
            size="small"
            sx={{ color: isBlocked ? '#f44336' : '#4caf50' }}
            onClick={() =>
              isBlocked
                ? handleUnBlockClickButton(validId)
                : handleBlockClickButton(validId)
            }
          >
            {isBlocked ? (
              <BlockIcon fontSize="small" />
            ) : (
              <CheckCircleIcon fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => <RowActions row={params.row} />,
    },
  ];

  // Format the manageStoreData for the DataGrid

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        {snackbarOpen && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </Alert>
          </Box>
        )}
        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2, // Optional: adds spacing between wrapped items
          }}
        >
          {/* Search - Left */}
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search by Store Name, Choice Group, or Store Type"
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none', // remove inner border
                },
                '& fieldset': {
                  border: 'none', // remove Material-UI default border
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px', // adjust this value to move the placeholder lower
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Action Buttons + Add Button - Right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {/* Bulk action buttons now conditional based on selected rows status */}
            <PermissionActionButtons
              module="choiceGroup"
              onBlock={showBlock ? handleBlockClick : undefined}
              onUnblock={showUnblock ? handleUnBlockClick : undefined}
              onDelete={selectedRows.length ? handleDeleteClick : undefined}
              blockLabel={showBlock ? 'Block' : undefined}
              unblockLabel={showUnblock ? 'Unblock' : undefined}
            />
            {hasPermission('choiceGroup', 'Add') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSubAdmin}
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: COLORS.primary.main,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: COLORS.primary.main,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                Add Choice Group
              </Button>
            )}
          </Box>
        </Box>

        <EditStoreOwnerModal
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          title="Add Choice Group"
        >
          <AddChoiceGroup onCancel={handleCloseAddDialog} />
        </EditStoreOwnerModal>

        <EditStoreOwnerModal
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          title={
            modalContent === 'add' ? 'Add Choice Group' : 'Edit Choice Group'
          }
        >
          <AddChoiceGroup onCancel={handleCloseEditDialog} />
        </EditStoreOwnerModal>

        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={ChoiceGroupData?.pagination?.totalItems}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
          }}
          onRowSelectionModelChange={(ids: string[]) => setSelectedRows(ids)}
          selectionModel={selectedRows}
        />
      </Box>
    </>
  );
};

export default ManageChoiceGroupTable;
